// DAS-2692: in-memory cache + background prefetch for the per-broker portfolio
// positions table served by GET /portfolio_by_account/<userId>/<account>.
//
// That endpoint is heavy, so opening a broker used to sit the user on a
// "Loading portfolio data..." spinner every time. We prefetch each broker's data
// in the background when the Portfolio modal opens and cache it, so clicking a
// broker opens instantly. Module-level cache (per tab, cleared on reload).

import api from './api';

const _cache = {};                 // account -> { data, debug, universe, ts }
const _inflight = {};              // account -> Promise (de-dupe concurrent fetches)
const FRESH_MS = 10 * 60 * 1000;   // a cached entry is usable for 10 minutes

/** Return a fresh cached entry for `account`, or null if absent/stale. */
export function getCachedAccount(account) {
  const e = _cache[account];
  if (!e) return null;
  if (Date.now() - e.ts > FRESH_MS) return null;
  return e;
}

/**
 * Fetch one broker's portfolio table and store it in the cache.
 * Returns the cached entry { data, debug, universe, ts }. Throws on failure.
 *
 * DAS-2720: de-duplicate concurrent requests for the same account. The background
 * preload (ToplineTable) and the modal open (AccountPortfolioModal, on a cache miss)
 * both call this. Without sharing, clicking a broker while its preload is still in
 * flight fired a SECOND heavy /portfolio_by_account call whose spinner outlived the
 * preload — so the header showed ✓ "ready" while the modal kept spinning (worst for
 * the last-prefetched broker, DeGiro Rebalance). Now a caller that arrives mid-flight
 * awaits the SAME promise, so the modal opens as soon as the preload finishes.
 */
export async function prefetchAccount(userId, account) {
  if (_inflight[account]) return _inflight[account];   // reuse an in-flight request
  const p = (async () => {
    const response = await api.get(`/portfolio_by_account/${userId}/${account}`);
    if (response?.data?.success) {
      const entry = {
        data: response.data.data,
        debug: response.data.debug,
        universe: response.data.select_now_universe,
        ts: Date.now(),
      };
      _cache[account] = entry;
      return entry;
    }
    throw new Error(`portfolio_by_account failed for ${account}`);
  })();
  _inflight[account] = p;
  try {
    return await p;
  } finally {
    delete _inflight[account];   // allow a later refresh once this settles
  }
}
